/**
 * Mapeia erros do Asaas (vindos via backend) pra mensagens amigáveis em PT-BR.
 * Portado do Reservei_Web (`src/lib/utils/asaas-errors.ts`) com adaptação de
 * shape: o client público do CC usa axios, então o payload de erro chega em
 * `err.response.data.error` ({ code, message, asaasErrorCode? }), enquanto o
 * client do Reservei usava `err.data`. Esta versão aceita os dois shapes.
 *
 * Padrões cobertos: cartão recusado, CVV inválido, validade, limite, dados inválidos.
 */

interface AsaasErrorPayload {
  code?: string;
  message?: string;
  asaasErrorCode?: string;
  httpStatus?: number;
}

interface BackendErrorShape {
  response?: { data?: { error?: AsaasErrorPayload } };
  data?: AsaasErrorPayload;
  message?: string;
}

const ASAAS_CODE_MAP: Record<string, string> = {
  invalid_credit_card: 'Cartão inválido. Verifique os dados e tente novamente.',
  invalid_credit_card_number: 'Número do cartão inválido. Verifique e tente novamente.',
  invalid_credit_card_holder_name: 'Nome do titular inválido.',
  invalid_credit_card_expiration: 'Validade do cartão inválida ou expirada.',
  invalid_credit_card_ccv: 'CVV inválido. Verifique o código de 3 dígitos no verso do cartão.',
  expired_card: 'Cartão expirado. Use outro cartão.',
  insufficient_funds: 'Saldo insuficiente. Tente outro cartão ou use PIX.',
  not_authorized: 'Cartão recusado pela operadora. Tente outro cartão ou use PIX.',
  card_declined: 'Cartão recusado. Entre em contato com a operadora ou tente outro cartão.',
  transaction_not_authorized:
    'Transação não autorizada pela operadora. Tente outro cartão ou use PIX.',
  invalid_cpf_cnpj: 'CPF/CNPJ inválido. Verifique os dados.',
  invalid_postal_code: 'CEP inválido.',
  invalid_address: 'Endereço inválido.',
  duplicate_payment: 'Já existe um pagamento em andamento. Aguarde alguns minutos.',
};

// Conflitos de cadastro do checkout público (public-checkout.service.ts).
const CHECKOUT_IDENTITY_CODES = new Set(['DOCUMENT_ALREADY_REGISTERED', 'EMAIL_DOCUMENT_MISMATCH']);

// Desfechos da tentativa de compra (checkout-attempt.service.ts). Vêm ANTES do
// mapa do Asaas: "resultado desconhecido" com o texto padrão ("tente novamente")
// levaria a pessoa a pagar duas vezes uma cobrança que pode já ter sido criada.
const CHECKOUT_ATTEMPT_MESSAGES: Record<string, string> = {
  CHECKOUT_RESULT_UNKNOWN:
    'Estamos confirmando o seu pagamento com o banco. Não pague de novo: assim que confirmar, seu plano é liberado.',
  CHECKOUT_ATTEMPT_CONFLICT:
    'Os dados mudaram depois que você começou a pagar. Recarregue a página e comece de novo.',
  CHECKOUT_ATTEMPT_NOT_RETRYABLE:
    'Essa compra não pode ser retomada. Recarregue a página e comece de novo.',
  PLAN_PRODUCT_UNSUPPORTED: 'Esse plano não pode ser comprado por aqui. Fale com o suporte.',
  // Fatura de cadastro sem documento: a pessoa não resolve digitando — sem este
  // código, o padrão /cpf|cnpj/ abaixo dizia "CPF/CNPJ inválido".
  CLIENT_MISSING_DOCUMENT: 'Seu cadastro está sem CPF ou CNPJ, e o banco exige. Fale com o suporte.',
  // Venda de serviço (peça 4): comprar de novo o mesmo serviço em andamento, ou
  // uma tela antiga pedindo ciclo que o plano não tem.
  SERVICE_ALREADY_ACTIVE: 'Você já tem esse serviço em andamento. Fale com o suporte se precisar de outro.',
  CYCLE_NOT_AVAILABLE: 'Esse plano mudou desde que você abriu a página. Recarregue e tente de novo.',
  PLAN_PRICE_MISSING: 'Esse serviço está sem valor cadastrado. Fale com o suporte.',
};

// Padrões de texto pra fallback quando código específico não vier
const TEXT_PATTERNS: Array<{ pattern: RegExp; message: string }> = [
  {
    pattern: /not_authorized|não autorizad|nao autorizad/i,
    message: 'Cartão recusado pela operadora. Tente outro cartão ou use PIX.',
  },
  {
    pattern: /insufficient|saldo insuf/i,
    message: 'Saldo ou limite insuficiente. Tente outro cartão ou use PIX.',
  },
  {
    pattern: /expired|expirad/i,
    message: 'Cartão expirado. Use outro cartão.',
  },
  {
    pattern: /invalid.*ccv|cvv|código de seguran/i,
    message: 'CVV inválido. Verifique o código no verso do cartão.',
  },
  {
    pattern: /invalid.*card|cartão inválid|cartao invalid/i,
    message: 'Cartão inválido. Verifique os dados e tente novamente.',
  },
  {
    pattern: /cpf|cnpj/i,
    message: 'CPF/CNPJ inválido. Verifique os dados.',
  },
  {
    pattern: /timeout|tempo esgotado/i,
    message: 'A operadora demorou para responder. Tente novamente em instantes.',
  },
];

export function mapAsaasError(err: unknown): string {
  const fallback = 'Não foi possível processar o pagamento. Tente novamente ou use PIX.';

  if (!err || typeof err !== 'object') return fallback;

  const e = err as BackendErrorShape;
  // Axios (CC): err.response.data.error — fallback pro shape antigo err.data
  const data = e.response?.data?.error ?? e.data;
  const code = data?.asaasErrorCode ?? data?.code;
  const message = data?.message || e.message || '';

  if (code && CHECKOUT_ATTEMPT_MESSAGES[code]) {
    return CHECKOUT_ATTEMPT_MESSAGES[code];
  }

  // 1. Tenta código específico do Asaas
  if (code && ASAAS_CODE_MAP[code]) {
    return ASAAS_CODE_MAP[code];
  }

  // 1b. Erro de CADASTRO do próprio checkout (não do Asaas): o texto do servidor
  // já é o motivo. Sem isso o padrão /cpf|cnpj/ abaixo trocava "Esse CPF/CNPJ já
  // está cadastrado com o e-mail …" por "CPF/CNPJ inválido" — motivo errado.
  if (code && CHECKOUT_IDENTITY_CODES.has(code) && data?.message) {
    return data.message;
  }

  // 2. Tenta padrões de texto na mensagem
  for (const { pattern, message: friendly } of TEXT_PATTERNS) {
    if (pattern.test(message) || (code && pattern.test(code))) {
      return friendly;
    }
  }

  // 3. Se a mensagem do backend já parece amigável (PT-BR), usa ela
  if (message && /[áéíóúçãõ]|cartão|pagamento|operadora/i.test(message)) {
    return message;
  }

  // 4. Fallback
  return fallback;
}
